import {
  Checkbox,
  DatePicker,
  Flex,
  Form,
  Input,
  message,
  Select,
  Spin,
} from "antd";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { encodeBase64File } from "../../../../../utils/encodeBase64";
import PatientService from "../../../../../Services/PatientService";
import { PlusOutlined } from "@ant-design/icons";
import { handleSetPatientRegistrationPayload } from "../../../../../Redux/actions/patientActions";
import dayjs from "dayjs";
import {
  fetchStateList,
  fetCountryList,
} from "../../../../../Redux/actions/CommonActions";

function Index({ handleCancel, handleSave, handleNext }) {
  const [profileImage, setProfileImage] = useState(null);
  const [firstLoad, setFirstLoad] = useState(false);
  const [dob, setDob] = useState();
  const [fields, setFields] = useState({
    firstname: "",
    lastname: "",
    city: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [rpmChecked, setRpmChecked] = useState(false);
  const [sosChecked, setSosChecked] = useState(false);
  const [currPageView, setCurrPageView] = useState(1);
  const [loader, setLoader] = useState(false);
  const formRef = useRef();
  const fileInputRef = useRef(null);
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { countries, states } = useSelector((state) => state.common);
  const { selectedEditPatient } = useSelector((state) => state.patients);
  const bloodgroup = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];
  const loggedUserObj = localStorage.getItem("login_result");
  const loggedUser = loggedUserObj ? JSON.parse(loggedUserObj) : null;

  useEffect(() => {
    if (!loggedUser) {
      return;
    }
    const payload = {
      status: 1,
    };
    dispatch(fetCountryList(payload));
    dispatch(fetchStateList(payload));
  }, []);

  useEffect(() => {
    if (selectedEditPatient) {
      setFirstLoad(true);

      formRef.current.setFieldsValue({
        patientId: selectedEditPatient?.patientId,
        userName: selectedEditPatient?.userName,
        salutation: selectedEditPatient?.salutation,
        firstname: selectedEditPatient?.firstName,
        lastname: selectedEditPatient?.lastName,
        email: selectedEditPatient?.email,
        mobile: selectedEditPatient?.mobile,
        gender: selectedEditPatient?.gender,
        maritalStatus: selectedEditPatient?.maritalStatus,
        bloodGroup: selectedEditPatient?.bloodGroup,
        addressOne: selectedEditPatient?.address1,
        addressTwo: selectedEditPatient?.address2,
        city: selectedEditPatient?.city,
        patientState: selectedEditPatient?.state,
        country: selectedEditPatient?.country,
        pincode: selectedEditPatient?.pinCode,
        dob: dayjs(selectedEditPatient?.dob),
        rpmChecked: selectedEditPatient?.rpmApplicable === "Y" ? true : false,
        sosChecked: selectedEditPatient?.sos === "Y" ? true : false,
        erContactNo1: selectedEditPatient?.erContactNo1,
        erContactNo2: selectedEditPatient?.erContactNo2,
        erContactNo3: selectedEditPatient?.erContactNo3,
        erContactNo4: selectedEditPatient?.erContactNo4,
        erContactNo5: selectedEditPatient?.erContactNo5,
      });
      setDob(selectedEditPatient?.dob);
      setRpmChecked(selectedEditPatient?.rpmApplicable === "Y" ? true : false);
      setSosChecked(selectedEditPatient?.sos === "Y" ? true : false);
      setFirstLoad(false);
    } else {
      setFirstLoad(false);
    }
  }, [selectedEditPatient]);

  useEffect(() => {
    if (fields.firstname) {
      const formattedFirstname =
        fields.firstname.charAt(0).toUpperCase() +
        fields.firstname.slice(1).toLowerCase();
      form.setFieldsValue({ firstname: formattedFirstname });
    }
    if (fields.lastname) {
      const formattedLastname =
        fields.lastname.charAt(0).toUpperCase() +
        fields.lastname.slice(1).toLowerCase();
      form.setFieldsValue({ lastname: formattedLastname });
    }

    if (fields.city) {
      const formattedCityName =
        fields.city.charAt(0).toUpperCase() +
        fields.city.slice(1).toLowerCase();
      form.setFieldsValue({ city: formattedCityName });
    }
  }, [fields]);

  const handleClick = () => {
    fileInputRef.current.click();
  };

  const handleUserPicChange = (e) => {
    const file = e.target.files[0];
    if (file && file.size <= 1048576) {
      setProfileImage(file);
    } else {
      message.error("Image/file size should be less than or equal to 1 mb !!");
      return;
    }
  };

  const handleDob = (value, valueString) => {
    setDob(valueString);
  };

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const validatePhoneNumber = (number) => /^\d{10}$/.test(number);

  const validatePincode = (pin) => /^\d{6}$/.test(pin);

  const handlePatientSave = async () => {
    const {
      salutation,
      firstname,
      lastname,
      email,
      mobile,
      gender,
      maritalStatus,
      bloodGroup,
      addressOne,
      addressTwo,
      city,
      patientState,
      country,
      pincode,
      erContactNo1,
      erContactNo2,
      erContactNo3,
      erContactNo4,
      erContactNo5,
    } = formRef.current.getFieldValue();
    const currDate = new Date().toISOString();
    const date = currDate.split("T")[0];
    const time = currDate.split("T")[1].slice(0, 8);
    const constructedTime = date + " " + time;

    if (!firstname || !lastname || !mobile) {
      message.error("Please fill in all mandatory fields");
      return;
    }

    if (!validatePhoneNumber(mobile)) {
      message.error("Please enter a valid 10-digit mobile number.");
      return;
    }

    if (email && !validateEmail(email)) {
      message.error("Please enter a valid mail id.");
      return;
    }
    if (pincode && !validatePincode(pincode)) {
      message.error("Please enter a valid pincode.");
      return;
    }

    let payload = {};

    if (sosChecked) {
      if (!erContactNo1 || !erContactNo2 || !erContactNo3 || !erContactNo4) {
        message.error("Please fill in all mandatory fields for SOS");
        return;
      }
    }

    if (selectedEditPatient) {
      payload = {
        ...selectedEditPatient,
        address1: addressOne ? addressOne : null,
        address2: addressTwo ? addressTwo : null,
        bloodGroup: bloodGroup ? bloodGroup : null,
        city: city ? city : null,
        country: country ? country : null,
        dob: dob ? dob : null,
        email: email ? email : null,
        firstName: firstname,
        gender,
        lastName: lastname,
        maritalStatus: maritalStatus ? maritalStatus : null,
        mobile,
        modifiedBy: loggedUser?.user?.userId,
        modifiedDate: constructedTime,
        pinCode: pincode ? pincode : null,
        salutation: salutation ? salutation : null,
        state: patientState ? patientState : null,
        status: 1,
        rpmApplicable: rpmChecked ? "Y" : "N",
        patientChronicConditionsList:
          selectedEditPatient?.patientChronicConditionsList,
        patientDevicesList: selectedEditPatient?.patientDevicesList,
        patientVitalsConfigList: selectedEditPatient?.patientVitalsConfigList,
        patientVitalsRangeList: selectedEditPatient?.patientVitalsRangeList,
        hospitalId: selectedEditPatient?.hospitalId,
        locationId: selectedEditPatient?.locationId,
        uhId: selectedEditPatient?.hospitalId,
        insuranceId1: selectedEditPatient?.insuranceId1,
        insuranceId2: selectedEditPatient?.insuranceId2,
        insuranceName1: selectedEditPatient?.insuranceName1,
        insuranceName2: selectedEditPatient?.insuranceName2,
        patientGroupNumber: selectedEditPatient?.patientGroupNumber,
        height: selectedEditPatient?.height,
        weight: selectedEditPatient?.weight,
        providerName: selectedEditPatient?.providerName,
        providerCode: selectedEditPatient?.providerCode,
        patientRegConsentDtlList: selectedEditPatient?.patientRegConsentDtlList,
        sos: sosChecked ? "Y" : "N",
        erContactNo1: erContactNo1
          ? erContactNo1
          : selectedEditPatient?.erContactNo1,
        erContactNo2: erContactNo2
          ? erContactNo2
          : selectedEditPatient?.erContactNo2,
        erContactNo3: erContactNo3
          ? erContactNo3
          : selectedEditPatient?.erContactNo3,
        erContactNo4: erContactNo4
          ? erContactNo4
          : selectedEditPatient?.erContactNo4,
        erContactNo5: erContactNo5 ? erContactNo5 : "",
      };
    } else {
      payload = {
        account_Id: loggedUser?.user?.account_Id,
        account_Name: null,
        address1: addressOne ? addressOne : null,
        address2: addressTwo ? addressTwo : null,
        bloodGroup: bloodGroup ? bloodGroup : null,
        city: city ? city : null,
        clinic_Id: loggedUser?.user?.clinic_Id,
        clinic_Name: null,
        country: country ? country : null,
        createdBy: loggedUser?.user?.userId,
        createdDate: constructedTime,
        dob: dob ? dob : null,
        email: email ? email : null,
        firstName: firstname,
        gender,
        lastName: lastname,
        loginId: null,
        loginIdAvailableYN: null,
        maritalStatus: maritalStatus ? maritalStatus : null,
        meetingLink: null,
        mobile,
        modifiedBy: loggedUser?.user?.userId,
        modifiedDate: constructedTime,
        parentPatientId: null,
        parentPatientName: null,
        patientId: null,
        patientRegFee: 0,
        patientRegFeeYN: null,
        photo: null,
        photoName: null,
        photoType: null,
        pinCode: pincode ? pincode : null,
        relationCode: null,
        relationDescription: null,
        salutation: salutation ? salutation : null,
        state: patientState ? patientState : null,
        status: 1,
        userId: loggedUser?.user?.userId,
        userName: loggedUser?.user?.userName,
        rpmApplicable: rpmChecked ? "Y" : "N",
        patientChronicConditionsList: [],
        patientDevicesList: [],
        patientVitalsConfigList: {
          bloodGlucose: 0,
          bmi: 1,
          diastolic: 1,
          heartRate: 0,
          height: 1,
          respiratoryRate: 0,
          spo2: 0,
          systolic: 1,
          temperature: 0,
          weight: 1,
          breathing: 1,
          pulse: 1,
          spirometry: 1,
          createdBy: "",
          modifiedBy: "",
          status: 1,
        },
        patientVitalsRangeList: [],
        hospitalId: "",
        locationId: "",
        uhId: "",
        insuranceId1: "",
        insuranceId2: "",
        insuranceName1: "",
        insuranceName2: "",
        patientGroupNumber: "",
        height: 0,
        weight: 0,
        providerName: "",
        providerCode: "",
        patientRegConsentDtlList: [],
        sos: sosChecked ? "Y" : "N",
        erContactNo1: erContactNo1 ? erContactNo1 : "",
        erContactNo2: erContactNo2 ? erContactNo2 : "",
        erContactNo3: erContactNo3 ? erContactNo3 : "",
        erContactNo4: erContactNo4 ? erContactNo4 : "",
        erContactNo5: erContactNo5 ? erContactNo5 : "",
      };
    }

    if (profileImage) {
      const res = await encodeBase64File(profileImage);
      payload.imageName = profileImage.name;
      if (profileImage?.type.includes("pdf")) {
        payload.photoType = "pdf";
        payload.photo = res;
      } else if (profileImage?.type.includes("png")) {
        payload.photoType = "png";
        payload.photo = res;
      } else if (profileImage?.type.includes("jpg")) {
        payload.photoType = "jpg";
        payload.photo = res;
      } else if (profileImage?.type.includes("jpeg")) {
        payload.photoType = "jpeg";
        payload.photo = res;
      }
    }

    if (rpmChecked) {
      dispatch(handleSetPatientRegistrationPayload(payload));
      handleNext();
    } else {
      if (selectedEditPatient) {
        setLoader(true);
        try {
          await PatientService.EditPatientData(payload);
          message.success("Patient Details updated successfully!!");
          setProfileImage(null);
          formRef.current.resetFields();
          setLoader(false);
          handleSave();
        } catch (e) {
          setLoader(false);
          console.error(e);
        }
      } else {
        setLoader(true);
        try {
          await PatientService.AddPatient(payload);
          message.success("Patient created successfully!!");
          setProfileImage(null);
          formRef.current.resetFields();
          handleSave();
          setLoader(false);
        } catch (e) {
          setLoader(false);
          console.error(e);
          e && message.error(e.response.data.message);
        }
      }
    }
  };

  const handleRpmChange = () => {
    setRpmChecked(!rpmChecked);
  };

  const handleSosChange = () => {
    setSosChecked(!sosChecked);
  };

  return (
    <>
      {firstLoad ? (
        <div className="min-h-[50vh] flex items-center justify-center mt-[10%]">
          <Flex align="center" gap="middle">
            <Spin size="large" tip="Loading" />
          </Flex>
        </div>
      ) : (
        <>
          <div>
            <Form
              ref={formRef}
              form={form}
              disabled={loader}
              className="w-full grid md:grid-cols-4 grid-cols-1 md:justify-center  md:items-center"
            >
              <div>
                <div className="flex flex-col m-auto" onClick={handleClick}>
                  <div className="Upload h-28 w-28 border-2 border-dashed p-2 rounded-full cursor-pointer relative">
                    <input
                      type="file"
                      style={{ display: "none" }}
                      onChange={handleUserPicChange}
                      ref={fileInputRef}
                    />
                    <div className="absolute  bottom-0 right-0 rounded-full text-center w-6 h-6 z-[1] p-1 bg-slate-300 cursor-pointer">
                      <div className="w-full flex justify-center mt-1 items-center">
                        <PlusOutlined />
                      </div>
                    </div>
                    {profileImage && (
                      <img
                        src={URL.createObjectURL(profileImage)}
                        alt="Profile"
                        className={`absolute top-0 left-0 w-full h-full object-contain rounded-full`}
                      />
                    )}
                    {!profileImage && selectedEditPatient?.photoName && (
                      <img
                        src={
                          process.env.REACT_APP_IMAGE_URL +
                          selectedEditPatient?.photoName
                        }
                        alt="Profile"
                        className={`absolute top-0 left-0 w-full h-full object-contain rounded-full`}
                      />
                    )}
                  </div>
                </div>
              </div>

              <div className="">
                <div className="">
                  <div className="">
                    <label className="md:text-xl text-base font-medium ">
                      Salutation
                    </label>
                  </div>
                  <Form.Item
                    name="salutation"
                    className="md:w-4/5 w-[inherit] md:p-2 p-1"
                  >
                    <Select
                      placeholder="Select Salutation"
                      className="md:h-8 h-8 outline-none border-2 rounded-lg"
                      allowClear
                    >
                      <Select.Option value={"Mr."}>Mr</Select.Option>
                      <Select.Option value={"Miss."}>Miss</Select.Option>
                      <Select.Option value={"Mrs."}>Mrs</Select.Option>
                    </Select>
                  </Form.Item>
                </div>
              </div>

              <div className="">
                <div className="">
                  <div className="">
                    <label className="md:text-lg text-base font-medium">
                      First Name
                      <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <Form.Item name="firstname">
                    <Input
                      type="text"
                      placeholder="FirstName"
                      name="firstname"
                      className="md:w-4/5 w-full md:h-8 h-8 md:p-2 outline-none border-2 p-1 rounded-lg"
                      onChange={(e) => {
                        const value = e.target.value;
                        const formattedValue =
                          value.charAt(0).toUpperCase() +
                          value.slice(1).toLowerCase();
                        setFields((prevState) => ({
                          ...prevState,
                          firstname: formattedValue,
                        }));
                      }}
                    />
                  </Form.Item>
                </div>
              </div>

              <div className="">
                <div className="">
                  <div className="">
                    <label className="md:text-lg text-base font-medium">
                      Last Name
                      <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <Form.Item name="lastname">
                    <Input
                      type="text"
                      placeholder="LastName"
                      name="lastname"
                      className="md:w-4/5 w-full md:h-8 h-8 md:p-2 outline-none border-2 p-1 rounded-lg"
                      onChange={(e) => {
                        const value = e.target.value;
                        const formattedValue =
                          value.charAt(0).toUpperCase() +
                          value.slice(1).toLowerCase();
                        setFields((prevState) => ({
                          ...prevState,
                          lastname: formattedValue,
                        }));
                      }}
                    />
                  </Form.Item>
                </div>
              </div>

              <div className="">
                <div className="">
                  <div className="">
                    <label className="md:text-lg text-base font-medium">
                      Mobile
                      <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <Form.Item name="mobile">
                    <Input
                      placeholder="Enter mobile No"
                      name="mobile"
                      className="md:w-4/5 w-full md:h-8 h-8 outline-none border-2 md:p-2 p-1 rounded-lg"
                      maxLength={10}
                      onKeyDown={(event) => {
                        const allowedKeys = [
                          "Backspace",
                          "Delete",
                          "Tab",
                          "ArrowLeft",
                          "ArrowRight",
                        ];

                        if (
                          !/[0-9]/.test(event.key) &&
                          !allowedKeys.includes(event.key)
                        ) {
                          event.preventDefault(); // Prevent non-numeric and non-control key input
                        }
                      }}
                    />
                  </Form.Item>
                </div>
              </div>

              <div className="">
                <div className="">
                  <div className="">
                    <label className="md:text-lg text-base font-medium">
                      Email
                    </label>
                  </div>
                  <Form.Item name="email">
                    <Input
                      type="email"
                      placeholder="Enter email"
                      name="email"
                      className="md:w-4/5 w-full  md:h-8 h-8 outline-none border-2 p-2 rounded-lg"
                    />
                  </Form.Item>
                </div>
              </div>

              <div className="">
                <div className="">
                  <div className="">
                    <label className="md:text-lg text-base font-medium">
                      Date Of Birth
                    </label>
                  </div>
                  <Form.Item name="dob" className="md:w-4/5 w-full py-2">
                    <DatePicker
                      placeholder="Date of birth"
                      format="MM/DD/YYYY"
                      className="w-full md:h-8 h-8 border-2 rounded-lg"
                      onChange={handleDob}
                      disabledDate={(current) =>
                        current && current.valueOf() > Date.now()
                      }
                    />
                  </Form.Item>
                </div>
              </div>

              <div className="">
                <div className="">
                  <div className="">
                    <label className="md:text-lg text-base font-medium">
                      Gender
                    </label>
                  </div>
                  <Form.Item name="gender" className="md:w-4/5 w-full py-2">
                    <Select
                      placeholder="Select Gender"
                      className="md:h-8 h-8 border-2 rounded-lg"
                      allowClear
                    >
                      <Select.Option value="M">Male</Select.Option>
                      <Select.Option value="F">Female</Select.Option>
                    </Select>
                  </Form.Item>
                </div>
              </div>

              <div className="">
                <div className="">
                  <div className="">
                    <label className="md:text-xl text-base font-medium ">
                      Marital Status
                    </label>
                  </div>
                  <Form.Item
                    name="maritalStatus"
                    className="md:w-4/5 md:py-2 py-1"
                  >
                    <Select
                      placeholder="Marital Status"
                      className="md:h-8 h-8 outline-none border-2 rounded-lg"
                      allowClear
                    >
                      <Select.Option value={"M"}>Married</Select.Option>
                      <Select.Option value={"U"}>Unmarried</Select.Option>
                    </Select>
                  </Form.Item>
                </div>
              </div>

              <div className="">
                <div className="">
                  <div className="">
                    <label className="md:text-xl text-base font-medium ">
                      Blood Group
                    </label>
                  </div>
                  <Form.Item
                    name="bloodGroup"
                    className="md:w-4/5 w-[inherit] md:py-2 py-1"
                  >
                    <Select
                      placeholder="Blood Group"
                      className="md:h-8 h-8 outline-none border-2 rounded-lg"
                      allowClear
                    >
                      {bloodgroup.map((option) => (
                        <Select.Option key={option} value={option}>
                          {option}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </div>
              </div>

              <div className="">
                <div className="">
                  <div className="">
                    <label className="md:text-lg text-base font-medium">
                      Address 1
                    </label>
                  </div>
                  <Form.Item name="addressOne">
                    <Input
                      placeholder="Address 1"
                      name="addressOne"
                      className="md:w-4/5 w-full md:h-8 h-8 outline-none border-2 md:p-2 p-1 rounded-lg"
                    />
                  </Form.Item>
                </div>
              </div>

              <div className="">
                <div className="">
                  <div className="">
                    <label className="md:text-lg text-base font-medium">
                      Address 2
                    </label>
                  </div>
                  <Form.Item name="addressTwo">
                    <Input
                      placeholder="Address 2"
                      name="addressTwo"
                      className="md:w-4/5 w-full md:h-8 h-8 outline-none border-2 md:p-2 p-1 rounded-lg"
                    />
                  </Form.Item>
                </div>
              </div>

              <div className="">
                <div className="">
                  <div className="">
                    <label className="md:text-lg text-base font-medium">
                      City
                    </label>
                  </div>
                  <Form.Item name="city">
                    <Input
                      placeholder="City"
                      name="city"
                      className="md:w-4/5 w-full md:h-8 h-8 outline-none border-2 md:p-2 p-1 rounded-lg"
                      onChange={(e) => {
                        const value = e.target.value;
                        const formattedValue =
                          value.charAt(0).toUpperCase() +
                          value.slice(1).toLowerCase();
                        setFields((prevState) => ({
                          ...prevState,
                          city: formattedValue,
                        }));
                      }}
                      onKeyDown={(event) => {
                        const allowedKeys = [
                          "Backspace",
                          "Delete",
                          "Tab",
                          "ArrowLeft",
                          "ArrowRight",
                        ];
                        if (
                          !/^[a-zA-Z]$/.test(event.key) &&
                          !allowedKeys.includes(event.key)
                        ) {
                          event.preventDefault();
                        }
                      }}
                    />
                  </Form.Item>
                </div>
              </div>

              <div className="">
                <div className="">
                  <div className="">
                    <label className="md:text-lg text-base font-medium">
                      State
                    </label>
                  </div>
                  <Form.Item name="patientState" className="md:w-4/5 w-full">
                    <Select
                      showSearch
                      className="bg-brand-lightBlue border-2 md:h-8 h-8 rounded-lg"
                      placeholder="Select State"
                      options={states?.map((state) => {
                        return {
                          label: state.stateName,
                          value: state.stateId,
                        };
                      })}
                      filterOption={(input, option) =>
                        option.label
                          .toLowerCase()
                          .indexOf(input.toLowerCase()) >= 0
                      }
                      allowClear
                    />
                  </Form.Item>
                </div>
              </div>

              <div className="">
                <div className="">
                  <div className="">
                    <label className="md:text-lg text-base font-medium">
                      Country
                    </label>
                  </div>
                  <Form.Item name="country" className="w-4/5">
                    <Select
                      className="bg-lightBlue border-2 md:h-8 h-8 rounded-lg"
                      placeholder="Select Country"
                      allowClear
                    >
                      {countries?.map((country) => (
                        <Select.Option value={country.country_Id}>
                          {country.country_Name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </div>
              </div>

              <div className="">
                <div className="">
                  <div className="">
                    <label className="md:text-lg text-base font-medium">
                      Pincode
                    </label>
                  </div>
                  <Form.Item name="pincode">
                    <Input
                      placeholder="Pincode"
                      name="pincode"
                      maxLength={6}
                      onKeyDown={(event) => {
                        const allowedKeys = [
                          "Backspace",
                          "Delete",
                          "Tab",
                          "ArrowLeft",
                          "ArrowRight",
                        ];

                        if (
                          !/[0-9]/.test(event.key) &&
                          !allowedKeys.includes(event.key)
                        ) {
                          event.preventDefault(); // Prevent non-numeric and non-control key input
                        }
                      }}
                      className="md:w-4/5 md:h-8 h-8 md:p-2 p-1 outline-none border-2 rounded-lg"
                    />
                  </Form.Item>
                </div>
              </div>

              <div className="">
                <div className="">
                  <div className="">
                    <label className="md:text-lg text-base font-medium">
                      RPM
                    </label>
                  </div>
                  <Form.Item valuePropName="checked" name="rpmChecked">
                    <Checkbox onChange={handleRpmChange} />
                  </Form.Item>
                </div>
              </div>

              {/* <div className="">
                <div className="">
                  <div className="">
                    <label className="md:text-lg text-base font-medium">
                      SOS
                    </label>
                  </div>
                  <Form.Item valuePropName="checked" name="sosChecked">
                    <Checkbox onChange={handleSosChange} />
                  </Form.Item>
                </div>
              </div> */}

              {sosChecked && (
                <>
                  <div className="">
                    <div className="">
                      <div className="">
                        <label className="md:text-lg text-base font-medium">
                          Contact 1<span className="text-red-500">*</span>
                        </label>
                      </div>
                      <Form.Item name="erContactNo1">
                        <Input
                          placeholder="Emergency Contact No 1"
                          name="mobile"
                          className="md:w-4/5 w-full md:h-8 h-8 outline-none border-2 md:p-2 p-1 rounded-lg"
                          maxLength={10}
                          onKeyDown={(event) => {
                            const allowedKeys = [
                              "Backspace",
                              "Delete",
                              "Tab",
                              "ArrowLeft",
                              "ArrowRight",
                            ];

                            if (
                              !/[0-9]/.test(event.key) &&
                              !allowedKeys.includes(event.key)
                            ) {
                              event.preventDefault(); // Prevent non-numeric and non-control key input
                            }
                          }}
                        />
                      </Form.Item>
                    </div>
                  </div>
                  <div className="">
                    <div className="">
                      <div className="">
                        <label className="md:text-lg text-base font-medium">
                          Contact 2<span className="text-red-500">*</span>
                        </label>
                      </div>
                      <Form.Item name="erContactNo2">
                        <Input
                          placeholder="Emergency Contact No 2"
                          name="mobile"
                          className="md:w-4/5 w-full md:h-8 h-8 outline-none border-2 md:p-2 p-1 rounded-lg"
                          maxLength={10}
                          onKeyDown={(event) => {
                            const allowedKeys = [
                              "Backspace",
                              "Delete",
                              "Tab",
                              "ArrowLeft",
                              "ArrowRight",
                            ];

                            if (
                              !/[0-9]/.test(event.key) &&
                              !allowedKeys.includes(event.key)
                            ) {
                              event.preventDefault(); // Prevent non-numeric and non-control key input
                            }
                          }}
                        />
                      </Form.Item>
                    </div>
                  </div>
                  <div className="">
                    <div className="">
                      <div className="">
                        <label className="md:text-lg text-base font-medium">
                          Contact 3<span className="text-red-500">*</span>
                        </label>
                      </div>
                      <Form.Item name="erContactNo3">
                        <Input
                          placeholder="Emergency Contact No 3"
                          name="mobile"
                          className="md:w-4/5 w-full md:h-8 h-8 outline-none border-2 md:p-2 p-1 rounded-lg"
                          maxLength={10}
                          onKeyDown={(event) => {
                            const allowedKeys = [
                              "Backspace",
                              "Delete",
                              "Tab",
                              "ArrowLeft",
                              "ArrowRight",
                            ];

                            if (
                              !/[0-9]/.test(event.key) &&
                              !allowedKeys.includes(event.key)
                            ) {
                              event.preventDefault(); // Prevent non-numeric and non-control key input
                            }
                          }}
                        />
                      </Form.Item>
                    </div>
                  </div>
                  <div className="">
                    <div className="">
                      <div className="">
                        <label className="md:text-lg text-base font-medium">
                          Contact 4<span className="text-red-500">*</span>
                        </label>
                      </div>
                      <Form.Item name="erContactNo4">
                        <Input
                          placeholder="Emergency Contact No 4"
                          name="mobile"
                          className="md:w-4/5 w-full md:h-8 h-8 outline-none border-2 md:p-2 p-1 rounded-lg"
                          maxLength={10}
                          onKeyDown={(event) => {
                            const allowedKeys = [
                              "Backspace",
                              "Delete",
                              "Tab",
                              "ArrowLeft",
                              "ArrowRight",
                            ];

                            if (
                              !/[0-9]/.test(event.key) &&
                              !allowedKeys.includes(event.key)
                            ) {
                              event.preventDefault(); // Prevent non-numeric and non-control key input
                            }
                          }}
                        />
                      </Form.Item>
                    </div>
                  </div>
                  <div className="">
                    <div className="">
                      <div className="">
                        <label className="md:text-lg text-base font-medium">
                          Contact 5 (Medical Contact)
                        </label>
                      </div>
                      <Form.Item name="erContactNo5">
                        <Input
                          placeholder="Emergency Contact No 5"
                          name="mobile"
                          className="md:w-4/5 w-full md:h-8 h-8 outline-none border-2 md:p-2 p-1 rounded-lg"
                          maxLength={10}
                          onKeyDown={(event) => {
                            const allowedKeys = [
                              "Backspace",
                              "Delete",
                              "Tab",
                              "ArrowLeft",
                              "ArrowRight",
                            ];

                            if (
                              !/[0-9]/.test(event.key) &&
                              !allowedKeys.includes(event.key)
                            ) {
                              event.preventDefault(); // Prevent non-numeric and non-control key input
                            }
                          }}
                        />
                      </Form.Item>
                    </div>
                  </div>
                </>
              )}
            </Form>
          </div>
          <div className="flex md:flex-row text-center flex-col md:items-center items-end justify-end gap-3 ">
            <button
              disabled={loader}
              className="md:min-w-24 w-[3rem] md:w-24  h-8 p-1 bg-white rounded-lg border text-brand-primaryColor border-brand-primaryColor font-semibold custom-button-shadow"
              onClick={handleCancel}
            >
              Cancel
            </button>
            <button
              disabled={loader}
              className="md:min-w-24 w-[3rem] md:w-24  h-8 p-1 custom_filter text-white rounded-lg font-semibold disabled:cursor-not-allowed disabled:opacity-50"
              onClick={handlePatientSave}
            >
              {rpmChecked || currPageView !== 1
                ? "Next"
                : selectedEditPatient
                ? "Update"
                : "Save"}
            </button>
          </div>
        </>
      )}
    </>
  );
}

export default Index;
